/**
 * Painel Comercial — /comercial/painel.
 *
 * DATA-DRIVEN (Painel configurável — Fase 2). Os cards deixam de ser hardcoded:
 * agora são renderizados a partir de um CATALOGO (`_catalogoComercial.jsx`) +
 * do LAYOUT salvo da unidade (`ConfiguracaoComercial.layout_painel`):
 *   - `ordem`   : ordem dos cards (∩ catálogo, resto do catálogo no fim);
 *   - `ocultos` : ids escondidos;
 *   - `tamanhos`: { id: spanString } — span por card (S/M/L).
 *
 * Quem tem `comercial:configurar` (ou Master) entra em "Personalizar layout":
 * arrasta para reordenar (HTML5 draggable), oculta (✕), redimensiona (ciclar
 * S/M/L) e re-adiciona via "Adicionar card". Persiste no backend com setState
 * imediato + PATCH debounce 600ms para /api/comercial/configuracoes/{id}
 * (espelha o mecanismo do Dashboard Financeiro). Quem não tem a permissão vê o
 * layout salvo, read-only (sem botão personalizar).
 *
 * Os tabchips de período + o fetch único de métricas (`obterPainel`) seguem
 * iguais. Com `layout_painel` vazio ({}) o painel renderiza TODOS os cards na
 * ordem do catálogo = visual de hoje (regressão-zero).
 *
 * Permissao de acesso: comercial:metricas. Permissao de editar: comercial:configurar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiMaximize2, FiPlus, FiSettings, FiSliders, FiX } from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useThemeToggle } from "../../../context/ThemeContext";
import { obterPainel } from "../../../services/comercialPainel";
import {
  atualizarConfiguracao,
  listarConfiguracoes,
} from "../../../services/comercialConfig";
import { salvarCSV, salvarExcel, salvarPDF } from "../../../utils/exportacao";
import { PageContainer } from "../../../styles/shared";
import ExportMenu from "../../Crm/ExportMenu";

import { fmtMoeda, fmtNumero } from "../../Orcamentos/helpers";
import { EmptyMsg, ErrorMsg } from "../../Orcamentos/styles";

import {
  CATALOGO_POR_ID,
  ExplicacaoDoCardContext,
  FONTES_POR_TIPO,
  FORMATOS_VALOR,
  PALETAS_GRAFICO,
  SPANS_CICLO,
  TIPOS_GRAFICO,
  layoutDefault,
  ocultoDefaultIds,
} from "./_catalogoComercial";
import { FONTE_PADRAO, resolverExplicacao } from "./explicacoes";
import { montarSecoesExportacao } from "./exportacao";

// --- Animacoes (framer-motion) — feel do Dashboard Financeiro ----------------
const EASE_ENTRADA = [0.16, 1, 0.3, 1];
const vConteudo = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2, ease: "easeIn" } },
};
const vGrupo = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};
const vItem = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: EASE_ENTRADA } },
};

// --- Periodos (tabchips). Default "mes". -------------------------------------
const PERIODOS = [
  { value: "semana", label: "Semana" },
  { value: "mes", label: "Mês" },
  { value: "trimestre", label: "Trimestre" },
  { value: "ano", label: "Ano" },
];

const PERIODO_LABEL = {
  semana: "Semana",
  mes: "Mês",
  trimestre: "Trimestre",
  ano: "Ano",
};

// Drag handle (espelha o DRAGGER do Dashboard Financeiro).
const DRAGGER = (
  <svg className="dragger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="9" cy="6" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="9" cy="12" r="1" />
    <circle cx="15" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="18" r="1" />
  </svg>
);

// Próximo span no ciclo S/M/L da categoria do card.
function proximoSpan(categoria, atual) {
  const ciclo = SPANS_CICLO[categoria] || SPANS_CICLO.kpi;
  const idx = ciclo.indexOf(Number(atual));
  return ciclo[(idx + 1) % ciclo.length];
}

// Limites de span para o resize por arrasto (alça na borda). A grade tem 12
// colunas; o minimo (3) preserva legibilidade de um KPI; o maximo (12) ocupa a
// linha. Independentes do ciclo S/M/L — o arrasto encaixa em QUALQUER inteiro.
const SPAN_MIN = 3;
const SPAN_MAX = 12;
const clampSpan = (n) => Math.max(SPAN_MIN, Math.min(SPAN_MAX, Math.round(n)));

// O card mostra a engrenagem de config se declara ao menos um ajuste suportado.
function cardTemAjustes(card) {
  const a = card?.ajustes;
  return Boolean(a && (a.fonte || a.formato || a.grafico));
}

// --- Pagina ------------------------------------------------------------------
function PainelComercial() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const { animacoes = true } = useThemeToggle() || {};
  const [periodo, setPeriodo] = useState("mes");
  const [estado, setEstado] = useState({ loading: true, error: null, dados: null });

  // Layout configurável da unidade.
  const [layout, setLayout] = useState(null); // null = carregando
  const [configId, setConfigId] = useState(null);
  const [editando, setEditando] = useState(false);
  const [catalogoAberto, setCatalogoAberto] = useState(false);
  const [configCardId, setConfigCardId] = useState(null); // card no SlideOver de ajustes
  const [dragOver, setDragOver] = useState(null);
  const [resizingId, setResizingId] = useState(null); // card sob resize por borda
  const dragId = useRef(null);
  // Arrastar começando no "?" não move o card (o "?" só abre a explicação).
  const toqueNoGatilhoAjuda = useRef(false);
  const saveTimer = useRef(null);
  const refetchTimer = useRef(null); // re-fetch apos trocar fonte (> debounce PATCH)
  const carregouLayout = useRef(false);
  const gridRef = useRef(null); // .widget-grid — base p/ calcular largura de coluna
  const resizeRef = useRef(null); // estado vivo do arrasto de borda

  const podeAcessar = isMaster || hasAllPermissions(["comercial:metricas"]);
  const podeEditar = isMaster || hasAllPermissions(["comercial:configurar"]);

  // --- Fetch único de métricas (igual ao painel atual) -----------------------
  const carregar = useCallback(async () => {
    setEstado((p) => ({ ...p, loading: true, error: null }));
    try {
      const dados = await obterPainel(request, { periodo });
      setEstado({ loading: false, error: null, dados });
    } catch (err) {
      setEstado({
        loading: false,
        error: err?.message || "Erro ao carregar painel.",
        dados: null,
      });
    }
  }, [request, periodo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // --- Carrega o layout da unidade UMA vez (guard de ref) ---------------------
  useEffect(() => {
    if (carregouLayout.current) return;
    carregouLayout.current = true;
    // silentError: o GET da config e gateado por comercial:view; quem tem
    // comercial:metricas mas nao comercial:view tomaria 403 -> toast espurio.
    // O .catch ja faz o fallback regressao-zero (layout vazio).
    listarConfiguracoes(request, { silentError: true })
      .then((payload) => {
        const items = Array.isArray(payload) ? payload : payload?.results || [];
        const cfg = items[0] || null;
        setConfigId(cfg?.id ?? null);
        setLayout(cfg?.layout_painel || {});
      })
      .catch(() => {
        setConfigId(null);
        setLayout({});
      });
  }, [request]);

  // --- Persistência: setState imediato + PATCH debounce 600ms -----------------
  // V15: o que está esperando o debounce fica aqui, para o unmount conseguir
  // descarregar. Sem isso, arrumar o painel e sair em menos de 600ms perde o
  // trabalho — e a tela não dá sinal nenhum.
  const pendenteRef = useRef(null);

  const persistir = useCallback(
    (novo) => {
      setLayout(novo);
      if (configId == null) return; // sem config (raro) — só estado local
      pendenteRef.current = novo;
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const corpo = pendenteRef.current;
        pendenteRef.current = null;
        atualizarConfiguracao(request, configId, { layout_painel: corpo })
          .catch((err) => {
            // Antes era `.catch(() => {})`: falhar em salvar o layout virava
            // silêncio absoluto. O `request` do AuthContext já mostra o
            // toast; aqui só garantimos que o erro não seja engolido.
            console.error("Falha ao salvar o layout do painel", err);
          });
      }, 600);
    },
    [request, configId]
  );

  // Flush no unmount — NÃO cancelamento.
  //
  // O comentário anterior dizia "a última edição em voo dispara o PATCH",
  // mas o código fazia `clearTimeout`, que faz o oposto: descartava. Quem
  // reorganizava o painel e saía da tela em menos de 600ms perdia tudo, sem
  // erro nenhum. Agora o pendente é enviado antes de sair.
  //
  // Via ref de propósito (mesmo padrão de RevisorFerragens/ListaSalva.jsx):
  // se o efeito dependesse da identidade de `persistir`, o cleanup rodaria a
  // cada render e o debounce viraria um PATCH por interação.
  const descarregarRef = useRef(null);
  descarregarRef.current = () => {
    clearTimeout(saveTimer.current);
    const corpo = pendenteRef.current;
    pendenteRef.current = null;
    if (corpo && configId != null) {
      atualizarConfiguracao(request, configId, { layout_painel: corpo })
        .catch((err) => {
          console.error("Falha ao salvar o layout do painel ao sair", err);
        });
    }
  };
  useEffect(
    () => () => {
      descarregarRef.current?.();
      clearTimeout(refetchTimer.current);
    },
    []
  );

  // --- Derivação: visíveis / ocultos / ordem ---------------------------------
  // `catalogoIds` = ids visiveis por padrao (layoutDefault ja exclui os
  // oculto_default da Fase 4). `ocultosDefault` = Set dos oculto_default. Regra
  // de visibilidade dos oculto_default: escondidos por padrao; so aparecem
  // quando o gestor os ADICIONA, gravado em `conf.mostrados` (ortogonal a
  // `ordem`/`ocultos` — sobrevive a reordenacao, que reescreve `ordem`). Hidden
  // efetivo = ocultos explicitos ∪ (oculto_default ainda NAO em `mostrados`).
  // Sem config, o painel mostra exatamente os 11 cards de hoje e lista os 4
  // novos em "Adicionar card" (regressao-zero).
  const catalogoIds = useMemo(() => layoutDefault(), []);
  const ocultosDefault = useMemo(() => ocultoDefaultIds(), []);
  const { visiveis, ocultos } = useMemo(() => {
    const conf = layout || {};
    const salva = (conf.ordem || []).filter((id) => CATALOGO_POR_ID[id]);
    const mostrados = new Set(
      (conf.mostrados || []).filter(
        (id) => CATALOGO_POR_ID[id] && ocultosDefault.has(id)
      )
    );
    // Universo na ordem: salvo + tail default (visiveis nao salvos) + os
    // oculto_default ja mostrados (p/ entrarem na grade) + os demais
    // oculto_default (vao p/ a lista de ocultos por padrao).
    const ordem = [
      ...salva,
      ...catalogoIds.filter((id) => !salva.includes(id)),
      ...[...ocultosDefault].filter((id) => !salva.includes(id)),
    ];
    const ocExpl = new Set(
      (conf.ocultos || []).filter((id) => CATALOGO_POR_ID[id])
    );
    // Oculto SE: ocultado explicitamente, OU oculto_default ainda nao mostrado.
    const estaOculto = (id) =>
      ocExpl.has(id) || (ocultosDefault.has(id) && !mostrados.has(id));
    return {
      visiveis: ordem.filter((id) => !estaOculto(id)),
      ocultos: ordem.filter((id) => estaOculto(id)),
    };
  }, [layout, catalogoIds, ocultosDefault]);

  // Span efetivo de um card (override em tamanhos[] ou span_default).
  const spanDe = useCallback(
    (id) => {
      const card = CATALOGO_POR_ID[id];
      if (!card) return 3;
      const override = (layout?.tamanhos || {})[id];
      const n = Number(override);
      return override && !Number.isNaN(n) ? n : card.span_default;
    },
    [layout]
  );

  // Opcoes de display de um card (formato/grafico) — passadas no ctx do
  // render. Fonte NAO vai aqui (e lida pelo backend, nao pelo render). Sem
  // config = objeto vazio -> render usa os defaults da Fase 2.
  const opcoesCardDe = useCallback(
    (id) => ({
      formato: (layout?.formatos || {})[id],
      grafico: (layout?.charts || {})[id],
    }),
    [layout]
  );

  // --- Mutações do layout -----------------------------------------------------
  const reordenar = (dropId) => {
    const from = visiveis.indexOf(dragId.current);
    const to = visiveis.indexOf(dropId);
    setDragOver(null);
    if (from < 0 || to < 0 || from === to) return;
    const nv = [...visiveis];
    const [m] = nv.splice(from, 1);
    nv.splice(to, 0, m);
    persistir({ ...(layout || {}), ordem: [...nv, ...ocultos] });
  };
  // Ocultar um card. Cards normais: entra em `conf.ocultos` (o tail do catalogo
  // os traria de volta, entao a entrada em ocultos e o que vale). Cards
  // oculto_default (Fase 4): basta sair de `conf.mostrados` para voltar ao
  // estado padrao escondido.
  const ocultar = (id) => {
    const base = layout || {};
    if (ocultosDefault.has(id)) {
      persistir({
        ...base,
        mostrados: (base.mostrados || []).filter((x) => x !== id),
      });
      return;
    }
    persistir({
      ...base,
      ocultos: [...new Set([...(base.ocultos || []), id])],
    });
  };
  // Mostrar (adicionar) um card. Cards normais: sai de `conf.ocultos`. Cards
  // oculto_default: entra em `conf.mostrados` (sinal explicito de "adicionado"
  // pelo gestor — sobrevive a reordenacao, ao contrario de gravar em `ordem`).
  const mostrar = (id) => {
    const base = layout || {};
    if (ocultosDefault.has(id)) {
      persistir({
        ...base,
        mostrados: [...new Set([...(base.mostrados || []), id])],
      });
      return;
    }
    persistir({
      ...base,
      ocultos: (base.ocultos || []).filter((x) => x !== id),
    });
  };
  const redimensionar = (id) => {
    const card = CATALOGO_POR_ID[id];
    if (!card) return;
    const novo = proximoSpan(card.categoria, spanDe(id));
    definirSpan(id, novo);
  };

  // Grava um span explicito (ciclo S/M/L e resize por borda compartilham isto).
  const definirSpan = useCallback(
    (id, span) => {
      const n = clampSpan(span);
      persistir({
        ...(layout || {}),
        tamanhos: { ...((layout || {}).tamanhos || {}), [id]: String(n) },
      });
    },
    [layout, persistir]
  );

  // --- Config por card (Fase 3): fonte / formato / grafico -------------------
  // Cada setter faz merge raso no sub-objeto do layout e persiste (debounce).
  const definirFonte = useCallback(
    (tipoFonte, valor) => {
      // tipoFonte = 'pipeline' | 'receita'. Grava layout_painel.fontes[tipo].
      const fontes = { ...((layout || {}).fontes || {}) };
      if (valor) fontes[tipoFonte] = valor;
      else delete fontes[tipoFonte];
      // AO SALVAR fonte, re-fetch: o backend le a fonte da config (cache ja
      // invalida na F3). setLayout imediato + PATCH debounce continuam valendo.
      persistir({ ...(layout || {}), fontes });
      // Pequeno atraso > debounce do PATCH (600ms) garante o backend ja com a
      // nova config no proximo GET; o carimbo de cache da F3 forca o miss.
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => carregar(), 700);
    },
    [layout, persistir, carregar]
  );

  const definirFormato = useCallback(
    (id, patch) => {
      const formatos = { ...((layout || {}).formatos || {}) };
      const atual = formatos[id] || {};
      formatos[id] = { ...atual, ...patch };
      persistir({ ...(layout || {}), formatos });
    },
    [layout, persistir]
  );

  const definirGrafico = useCallback(
    (id, patch) => {
      const charts = { ...((layout || {}).charts || {}) };
      const atual = charts[id] || {};
      charts[id] = { ...atual, ...patch };
      persistir({ ...(layout || {}), charts });
    },
    [layout, persistir]
  );

  // --- Resize por arrasto da alça na borda direita (Fase 3) ------------------
  // Pointer events: onPointerDown na alça captura o estado inicial; move/up no
  // window. Largura de 1 coluna = (largura do grid - 11*gap)/12. deltaX vira
  // delta de colunas, clamp 3..12, snap no inteiro. Só grava (definirSpan) no
  // up se mudou — evita PATCH a cada pixel.
  const colunaPx = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return 0;
    const styles = window.getComputedStyle(grid);
    const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
    const w = grid.clientWidth;
    return (w - gap * (SPAN_MAX - 1)) / SPAN_MAX;
  }, []);

  // Refs estaveis para o resize. O preview chama setLayout a cada coluna; se os
  // handlers dependessem de `layout` eles seriam recriados no meio do arrasto e
  // o cleanup do effect soltaria os listeners (o drag congelava apos 1 coluna).
  // Mantendo handlers estaveis (deps []) + lendo layout/persistir de refs, o
  // arrasto roda do inicio ao fim e persiste no release.
  const layoutRef = useRef(null);
  const persistRef = useRef(persistir);
  const onResizeMoveRef = useRef(null);
  const onResizeUpRef = useRef(null);
  useEffect(() => {
    layoutRef.current = layout;
    persistRef.current = persistir;
  });

  const onResizeMove = useCallback((e) => {
    const st = resizeRef.current;
    if (!st) return;
    const col = st.colPx || 1;
    const deltaCols = Math.round((e.clientX - st.startX) / col);
    const alvo = clampSpan(st.startSpan + deltaCols);
    if (alvo !== st.spanPreview) {
      st.spanPreview = alvo;
      // Preview imediato no estado (sem PATCH): atualiza só o tamanho local.
      setLayout((prev) => ({
        ...(prev || {}),
        tamanhos: { ...((prev || {}).tamanhos || {}), [st.id]: String(alvo) },
      }));
    }
  }, []);

  const onResizeUp = useCallback(() => {
    const st = resizeRef.current;
    window.removeEventListener("pointermove", onResizeMoveRef.current);
    window.removeEventListener("pointerup", onResizeUpRef.current);
    window.removeEventListener("pointercancel", onResizeUpRef.current);
    document.body.classList.remove("comercial-painel-resizing");
    resizeRef.current = null;
    setResizingId(null);
    // O preview ja aplicou o span final no layout local; agenda o PATCH.
    if (st && layoutRef.current) persistRef.current(layoutRef.current);
  }, []);

  onResizeMoveRef.current = onResizeMove;
  onResizeUpRef.current = onResizeUp;

  const iniciarResize = useCallback(
    (e, id) => {
      if (!editando) return;
      e.preventDefault();
      e.stopPropagation();
      const startSpan = spanDe(id);
      resizeRef.current = {
        id,
        startX: e.clientX,
        startSpan,
        spanPreview: startSpan,
        colPx: colunaPx(),
      };
      setResizingId(id);
      document.body.classList.add("comercial-painel-resizing");
      window.addEventListener("pointermove", onResizeMove);
      window.addEventListener("pointerup", onResizeUp);
      window.addEventListener("pointercancel", onResizeUp);
    },
    [editando, spanDe, colunaPx, onResizeMove, onResizeUp]
  );

  // Higiene: SO no unmount (deps []) — nunca por troca de identidade de
  // callback, senao o cleanup soltaria os listeners no meio do arrasto.
  useEffect(
    () => () => {
      window.removeEventListener("pointermove", onResizeMoveRef.current);
      window.removeEventListener("pointerup", onResizeUpRef.current);
      window.removeEventListener("pointercancel", onResizeUpRef.current);
      document.body.classList.remove("comercial-painel-resizing");
    },
    []
  );

  // Sair do modo personalizar fecha os sub-paineis (config/adicionar) abertos.
  useEffect(() => {
    if (!editando) {
      setConfigCardId(null);
      setCatalogoAberto(false);
    }
  }, [editando]);

  const d = estado.dados;

  // --- Derivacoes de dados (com fallback neutro p/ chaves novas) -------------
  const cab = d?.cabecalho;
  const ex = useMemo(() => d?.kpis_extra || {}, [d]);
  const ranking = useMemo(() => (d?.ranking_vendedores || []).slice(0, 5), [d]);
  // `por_que_perdemos` ja respeita a fonte configurada (default "lead") e e a
  // fonte canonica das razoes de perda. NAO cair em `razoes_perda` (sempre
  // orcamento/RazaoPerdaOrcamento) para nao misturar fontes no mesmo widget.
  const perdas = useMemo(() => d?.por_que_perdemos?.razoes || [], [d]);
  const totalAnalises = d?.por_que_perdemos?.total ?? 0;
  const funil = d?.funil_leads;
  const subPeriodo = PERIODO_LABEL[periodo] || "Mês";

  // Contexto passado para cada card.render(d, ctx).
  const ctx = useMemo(
    () => ({
      ex,
      cab,
      funil,
      ranking,
      perdas,
      totalAnalises,
      subPeriodo,
      periodo,
      PERIODO_LABEL,
      animacoes,
    }),
    [ex, cab, funil, ranking, perdas, totalAnalises, subPeriodo, periodo, animacoes]
  );

  // --- Subtitulo dinamico (cabecalho) ----------------------------------------
  // Meta unica da unidade, proporcional ao periodo (ADR-0079). Antes o
  // subtitulo trazia "time UN1" fixo para qualquer unidade.
  const pageSub = cab
    ? [
        cab.mes_label,
        Number(cab.meta) > 0
          ? `${cab.meta_rotulo || "meta"} ${fmtMoeda(cab.meta)} · ${fmtNumero(cab.pct_meta, 0)}% atingido`
          : "sem meta — defina em Comercial › Configurações",
      ].join(" · ")
    : "Funil, conversão, ticket, pipeline e performance";

  // Fontes em uso: as que o backend resolveu (fonte da verdade do numero),
  // com o layout local como reserva enquanto o payload nao chegou.
  const fontesEmUso = useMemo(
    () => ({ ...(layout?.fontes || {}), ...(d?.fontes || {}) }),
    [layout, d]
  );

  // --- Exportacao: o que esta na tela (ADR-0079) ------------------------------
  const handleExportar = useCallback((formato) => {
    if (!d) return;
    const payload = {
      titulo: `Painel Comercial — ${subPeriodo}`,
      resumo: [
        { label: "Período", value: subPeriodo, type: "text" },
        {
          label: cab?.meta_rotulo || "Meta",
          value: Number(cab?.meta) > 0 ? Number(cab.meta) : "sem meta",
          type: Number(cab?.meta) > 0 ? "currency" : "text",
        },
        {
          label: "% atingido",
          value: cab?.pct_meta != null ? `${fmtNumero(cab.pct_meta, 0)}%` : "—",
          type: "text",
        },
      ],
      secoes: montarSecoesExportacao({
        visiveis,
        d,
        ctx,
        rotulo: (id) => CATALOGO_POR_ID[id]?.label || id,
        regua: (id) => resolverExplicacao(id, fontesEmUso)?.regua,
      }),
    };
    const SAVERS = { pdf: salvarPDF, excel: salvarExcel, csv: salvarCSV };
    (SAVERS[formato] || salvarCSV)(payload);
  }, [d, ctx, cab, subPeriodo, visiveis, fontesEmUso]);

  // --- Guardas (permissao / loading / erro) ----------------------------------
  if (!podeAcessar) {
    return (
      <PageContainer className="comercial-painel">
        <EmptyMsg>Você não tem permissão para acessar métricas comerciais.</EmptyMsg>
      </PageContainer>
    );
  }

  // Renderiza UM card (chrome de edição + conteúdo do catálogo).
  const renderCard = (id) => {
    const card = CATALOGO_POR_ID[id];
    if (!card || !d) return null;
    const span = spanDe(id);
    const ehKpi = card.categoria === "kpi";
    const temAjustes = cardTemAjustes(card);
    // ctx por card: o compartilhado + as opcoes de display deste card.
    const ctxCard = { ...ctx, opcoesCard: opcoesCardDe(id) };
    // A explicacao segue a fonte em uso (layout_painel.fontes), igual ao numero.
    const explicacaoDoCard = {
      titulo: card.label,
      explicacao: resolverExplicacao(id, fontesEmUso),
    };
    // KPI: chrome .widget kpi-widget aqui (conteúdo = head/val/delta do catálogo).
    // Faixa: o catálogo já devolve o .block inteiro; aqui só o wrapper + span.
    const className = `${ehKpi ? "widget kpi-widget " : ""}painel-card span-${span}`;
    return (
      <motion.div
        key={id}
        className={className}
        variants={vItem}
        style={{ position: "relative" }}
        draggable={editando && resizingId == null}
        onPointerDownCapture={(e) => {
          toqueNoGatilhoAjuda.current = Boolean(
            e.target.closest?.(".explicacao-card__gatilho")
          );
        }}
        onDragStart={(e) => {
          if (toqueNoGatilhoAjuda.current) {
            e.preventDefault();
            return;
          }
          dragId.current = id;
        }}
        onDragOver={(e) => {
          if (editando) {
            e.preventDefault();
            setDragOver(id);
          }
        }}
        onDragLeave={() =>
          editando && setDragOver((cur) => (cur === id ? null : cur))
        }
        onDrop={() => editando && reordenar(id)}
        data-dragover={editando && dragOver === id ? "1" : undefined}
        data-resizing={resizingId === id ? "1" : undefined}
      >
        {editando && (
          <div className="painel-card__edit" onClick={(e) => e.stopPropagation()}>
            <span className="painel-card__drag" title="Arraste para reordenar">
              {DRAGGER}
            </span>
            {temAjustes && (
              <button
                type="button"
                className="painel-card__btn"
                title="Configurar card"
                aria-label="Configurar card"
                onClick={() => setConfigCardId(id)}
              >
                <FiSliders size={13} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="painel-card__btn"
              title="Redimensionar (S/M/L)"
              onClick={() => redimensionar(id)}
            >
              <FiMaximize2 size={13} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="painel-card__btn danger"
              title="Ocultar card"
              onClick={() => ocultar(id)}
            >
              <FiX size={14} aria-hidden="true" />
            </button>
          </div>
        )}
        <ExplicacaoDoCardContext.Provider value={explicacaoDoCard}>
          {card.render(d, ctxCard)}
        </ExplicacaoDoCardContext.Provider>
        {editando && (
          <span
            className="painel-card__resize"
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionar arrastando a borda"
            title="Arraste para redimensionar"
            onPointerDown={(e) => iniciarResize(e, id)}
          />
        )}
      </motion.div>
    );
  };

  // --- Render ----------------------------------------------------------------
  return (
    <PageContainer className="comercial-painel">
      {/* Header: breadcrumb + titulo + sub | tabchips + Personalizar + Exportar */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="breadcrumb">
            <span className="crumb">Início</span>
            <span className="crumb-sep">/</span>
            <span className="crumb">Comercial</span>
            <span className="crumb-sep">/</span>
            <span className="crumb active">Painel</span>
          </div>
          <h1 className="page-title">Painel Comercial</h1>
          <div className="page-sub">
            {editando ? (
              <strong style={{ color: "var(--accent)" }}>
                arraste p/ reordenar · arraste a borda p/ redimensionar · ⤢ S/M/L
                · ⚙ configura · ✕ oculta
              </strong>
            ) : (
              pageSub
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div className="tabchip-group" role="tablist" aria-label="Período">
            {PERIODOS.map((p) => (
              <div
                key={p.value}
                role="tab"
                tabIndex={0}
                aria-selected={periodo === p.value}
                className={`tabchip${periodo === p.value ? " active" : ""}`}
                onClick={() => setPeriodo(p.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setPeriodo(p.value);
                  }
                }}
              >
                {p.label}
              </div>
            ))}
          </div>
          {podeEditar && layout != null && (
            <button
              type="button"
              className={`btn btn--sm btn--icon ${editando ? "btn--primary" : "btn--secondary"}`}
              onClick={() => setEditando((e) => !e)}
              aria-label="Personalizar painel"
              aria-pressed={editando}
              title={editando ? "Concluir personalização" : "Personalizar painel"}
            >
              <FiSettings size={15} aria-hidden="true" />
            </button>
          )}
          <ExportMenu onExportar={handleExportar} disabled={!d} />
        </div>
      </div>

      {estado.error && <ErrorMsg>{estado.error}</ErrorMsg>}

      <AnimatePresence mode="wait">
        {estado.loading || layout == null ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <EmptyMsg>Carregando métricas...</EmptyMsg>
          </motion.div>
        ) : !d ? (
          <motion.div
            key="vazio"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <EmptyMsg>Sem dados.</EmptyMsg>
          </motion.div>
        ) : (
          <motion.div
            key={`painel-${periodo}`}
            variants={vConteudo}
            initial={animacoes ? "hidden" : false}
            animate="show"
            exit="exit"
            style={{ width: "100%" }}
          >
            <motion.div
              ref={gridRef}
              className={`widget-grid${editando ? " is-editing" : ""}`}
              variants={vGrupo}
            >
              {visiveis.map(renderCard)}

              {editando && (
                <div
                  className="painel-add span-12"
                  role="button"
                  tabIndex={0}
                  onClick={() => setCatalogoAberto(true)}
                  onKeyDown={(e) => e.key === "Enter" && setCatalogoAberto(true)}
                >
                  <div className="painel-add__icon">
                    <FiPlus size={16} aria-hidden="true" />
                  </div>
                  <div>Adicionar card</div>
                  <div className="tiny muted">
                    {ocultos.length
                      ? `${ocultos.length} disponível(is)`
                      : "todos no painel"}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slide-over: Adicionar card (restaura ocultos) */}
      <SlideOverPanel
        open={catalogoAberto}
        onClose={() => setCatalogoAberto(false)}
        title="Adicionar card"
        subtitle="Cards ocultos disponíveis para o painel"
        width="min(95vw, 460px)"
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          {ocultos.length === 0 ? (
            <EmptyMsg>Todos os cards já estão no painel.</EmptyMsg>
          ) : (
            ocultos.map((id) => {
              const card = CATALOGO_POR_ID[id];
              if (!card) return null;
              return (
                <div
                  key={id}
                  className="flex between"
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid var(--divider)",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{card.label}</div>
                    <div className="tiny muted">
                      {card.categoria === "kpi" ? "Indicador" : "Faixa analítica"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    onClick={() => mostrar(id)}
                  >
                    <FiPlus size={13} aria-hidden="true" /> Adicionar
                  </button>
                </div>
              );
            })
          )}
          <div className="tiny muted" style={{ marginTop: 16 }}>
            Para ocultar um card, use o ✕ no modo personalizar.
          </div>
        </div>
      </SlideOverPanel>

      {/* Slide-over: Configurar card (fonte / formato / grafico) */}
      <SlideOverPanel
        open={configCardId != null}
        onClose={() => setConfigCardId(null)}
        title={
          configCardId
            ? `Configurar · ${CATALOGO_POR_ID[configCardId]?.label || ""}`
            : "Configurar card"
        }
        subtitle="Ajustes aplicados só a este card (salvos automaticamente)"
        width="min(95vw, 420px)"
      >
        {configCardId && (
          <ConfigCardForm
            card={CATALOGO_POR_ID[configCardId]}
            layout={layout || {}}
            onFonte={definirFonte}
            onFormato={definirFormato}
            onGrafico={definirGrafico}
          />
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}

// --- Form de config por card (Fase 3) ---------------------------------------
// Mostra SO os controles aplicaveis ao card (lê card.ajustes). Cada controle
// grava direto no layout via os setters do pai (debounce + re-fetch p/ fonte).
function ConfigCardForm({ card, layout, onFonte, onFormato, onGrafico }) {
  const id = card.id;
  const a = card.ajustes || {};
  const formatoAtual = (layout.formatos || {})[id] || {};
  const graficoAtual = (layout.charts || {})[id] || {};
  // Fonte efetiva (default conhecido por tipo quando ausente).
  // `a.fonte` = chave de OPCOES em FONTES_POR_TIPO ('pipeline'|'receita'|
  // 'taxa_conversao'|'ticket'). `a.fonteKey` = chave PERSISTIDA em
  // layout_painel.fontes (default = a.fonte). Sao desacoplados porque o
  // catalogo de opcoes do ticket vive sob 'ticket' mas o backend le
  // 'ticket_medio' — entao o card declara fonte:'ticket', fonteKey:'ticket_medio'.
  const fonteTipo = a.fonte; // chave de opcoes (FONTES_POR_TIPO)
  const fonteKey = a.fonteKey || a.fonte; // chave persistida (layout.fontes[...])
  const fonteOpcoes = fonteTipo ? FONTES_POR_TIPO[fonteTipo] || [] : [];
  const fonteAtual = fonteKey ? (layout.fontes || {})[fonteKey] || "" : "";
  // O padrao do sistema e a opcao recomendada; o rotulo diz qual e (ADR-0079).
  const fontePadrao = fonteOpcoes.find((o) => o.value === FONTE_PADRAO[fonteKey]);
  const outrasFontes = fonteOpcoes.filter((o) => o !== fontePadrao);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* FONTE (cards de valor) */}
      {fonteTipo && (
        <div className="input-group">
          <label className="input-label" htmlFor="cfg-fonte">
            Fonte do valor
          </label>
          <select
            id="cfg-fonte"
            className="select"
            value={fonteAtual}
            onChange={(e) => onFonte(fonteKey, e.target.value)}
          >
            <option value="">
              {fontePadrao
                ? `Padrão do sistema — ${fontePadrao.label} (recomendado)`
                : "Padrão do sistema"}
            </option>
            {outrasFontes.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="input-help">
            Trocar a fonte recarrega as métricas — o cálculo vem do backend.
          </span>
        </div>
      )}

      {/* FORMATO (cards de valor) */}
      {a.formato && (
        <div className="input-group">
          <label className="input-label" htmlFor="cfg-formato">
            Formato do valor
          </label>
          <select
            id="cfg-formato"
            className="select"
            value={formatoAtual.tipo || ""}
            onChange={(e) => onFormato(id, { tipo: e.target.value || undefined })}
          >
            <option value="">Padrão (R$ compacto)</option>
            {FORMATOS_VALOR.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <label
            className="input-label"
            htmlFor="cfg-casas"
            style={{ marginTop: 6 }}
          >
            Casas decimais
          </label>
          <select
            id="cfg-casas"
            className="select"
            value={
              Number.isFinite(Number(formatoAtual.casas))
                ? String(formatoAtual.casas)
                : ""
            }
            onChange={(e) =>
              onFormato(id, {
                casas: e.target.value === "" ? undefined : Number(e.target.value),
              })
            }
          >
            <option value="">Padrão</option>
            {[0, 1, 2, 3, 4].map((c) => (
              <option key={c} value={String(c)}>
                {c}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* GRAFICO (cards com sparkline) */}
      {a.grafico && (
        <div className="input-group">
          <label className="input-label" htmlFor="cfg-grafico-tipo">
            Tipo de gráfico
          </label>
          <select
            id="cfg-grafico-tipo"
            className="select"
            value={graficoAtual.tipo || ""}
            onChange={(e) =>
              onGrafico(id, { tipo: e.target.value || undefined })
            }
          >
            <option value="">Padrão (linha)</option>
            {TIPOS_GRAFICO.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <label
            className="input-label"
            htmlFor="cfg-grafico-paleta"
            style={{ marginTop: 6 }}
          >
            Cor (paleta)
          </label>
          <div className="painel-cfg-paletas" role="radiogroup" aria-label="Cor do gráfico">
            {PALETAS_GRAFICO.map((p) => {
              const ativa = Number(graficoAtual.paleta) === p;
              return (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={ativa}
                  aria-label={`Paleta ${p}`}
                  className={`painel-cfg-paleta${ativa ? " is-active" : ""}`}
                  style={{ background: `var(--chart-${p})` }}
                  onClick={() =>
                    onGrafico(id, { paleta: ativa ? undefined : p })
                  }
                />
              );
            })}
          </div>
          <span className="input-help">
            Clique de novo na cor ativa para voltar ao padrão.
          </span>
        </div>
      )}
    </div>
  );
}

export default PainelComercial;
